import { useState } from 'react'
import { Check } from 'lucide-react'

/** Parse one `- ...` line from a concierge-options fenced block.
 *
 * Supports two enhancement markers (Phase 2):
 *   `Label (default)`          — marks the agent's recommended pick
 *   `Label — rationale text`    — em-dash + spaces splits label from reason
 *
 * Backwards compatible: bare `Label` parses to
 * `{ label: 'Label', isDefault: false, rationale: null }`.
 *
 * Mirrors `parseConciergeOption` in the Obsidian plugin's main.js. */
function parseOption(raw: string): { label: string; isDefault: boolean; rationale: string | null } {
  let line = raw.trim()
  let rationale: string | null = null
  const dashIdx = line.indexOf(' — ')
  if (dashIdx >= 0) {
    rationale = line.slice(dashIdx + 3).trim() || null
    line = line.slice(0, dashIdx).trim()
  }
  let isDefault = false
  const m = line.match(/^(.*?)\s*\(default\)\s*$/i)
  if (m) {
    isDefault = true
    line = m[1].trim()
  }
  return { label: line, isDefault, rationale }
}

interface Props {
  /** Raw text content of the fenced `concierge-options` block. Each line
   *  starting with `- ` becomes one button option. */
  body: string
  /** Stage the chosen text into the chat composer (don't auto-send). The
   *  user can then edit or extend the staged text before pressing Enter,
   *  or just press Enter immediately for the fast flow. The second arg
   *  is this card's PREVIOUS staged value; when present in the composer,
   *  the parent swaps it in place (so re-picking inside this card doesn't
   *  wipe other cards' contributions). Omit on the first pick. */
  onStage: (chosen: string, replacePrevious?: string) => void
  /** True only for the most-recent assistant turn while no stream is in
   *  flight. Historical / superseded cards still render their buttons
   *  (so the transcript doesn't collapse to raw text) but the controls
   *  are frozen and the chosen pill is highlighted as a record. */
  isLive: boolean
  /** Content of the user turn immediately after this assistant turn, if
   *  any. Used to recover which option was chosen across a page reload
   *  (component-local `chosen` state is wiped when the message remounts,
   *  but the next-user-turn content is the actual decision record). */
  nextUserContent?: string
}

/**
 * Clickable option card. Replaces the default code-block rendering for
 *
 *     ```concierge-options
 *     - Option A
 *     - Option B
 *     ```
 *
 * Behavior:
 *   - The card ALWAYS renders, regardless of whether the assistant turn
 *     is current or historical. The previous gate ("only render the card
 *     on the most-recent turn") collapsed the card to raw text the
 *     instant a new turn was appended — exactly the "revert to raw text"
 *     regression the user reported.
 *   - On the live turn, clicking a button stages the chosen text in the
 *     composer (does not auto-send). The user presses Enter to send, or
 *     types to add context first.
 *   - On historical turns, buttons stay visible but are inert. The
 *     option that matches the next user turn's content is highlighted
 *     with a check so the decision history is readable at a glance.
 *
 * Mirrors `_renderOptionCards` in the Obsidian plugin's main.js for the
 * parsing rules.
 */
export function ConciergeOptionCard({
  body,
  onStage,
  isLive,
  nextUserContent,
}: Props) {
  const [localChosen, setLocalChosen] = useState<string | null>(null)
  const [other, setOther] = useState('')
  // Remember what this card last staged so re-picks can replace in place
  // (vs. appending another copy to the composer). Distinct from
  // `localChosen` because the freeform "other" input also contributes.
  const [lastStaged, setLastStaged] = useState<string | null>(null)

  const options = body
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('-'))
    .map((l) => l.replace(/^-\s*/, '').trim())
    .filter(Boolean)
    .map(parseOption)

  // Malformed block (no dashed list lines). Render the body as a plain
  // <pre> so the agent's intent isn't silently swallowed.
  if (options.length === 0) {
    return (
      <pre className="concierge-option-card-fallback">
        <code>{body}</code>
      </pre>
    )
  }

  // Switch to vertical stack layout when any option carries (default) or
  // rationale, so rationale can render beneath each label.
  const hasEnhanced = options.some((o) => o.isDefault || o.rationale)

  // Resolve the chosen option (by label, not the raw line):
  //   1. Local click in this session takes precedence.
  //   2. Fall back to matching nextUserContent against the option labels
  //      (exact match, or startsWith for "chose option, then added
  //      context" edits in the composer before sending).
  //   3. Otherwise no option is marked chosen.
  let chosen: string | null = localChosen
  if (!chosen && nextUserContent) {
    const trimmedNext = nextUserContent.trim()
    chosen =
      options.find((o) => o.label === trimmedNext)?.label ??
      options.find((o) => trimmedNext.startsWith(o.label))?.label ??
      null
  }

  // Live cards stay interactive even after a click — clicking a second
  // option just re-stages it in the composer (replacing the prior pick).
  // Historical / superseded cards are frozen.
  const interactive = isLive

  const fire = (text: string) => {
    if (!interactive) return
    setLocalChosen(text)
    onStage(text, lastStaged ?? undefined)
    setLastStaged(text)
  }

  const onOtherKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && other.trim()) {
      e.preventDefault()
      fire(other.trim())
    }
  }

  return (
    <div
      className="concierge-option-card"
      aria-disabled={!interactive}
      data-state={chosen ? 'answered' : isLive ? 'live' : 'frozen'}
      data-layout={hasEnhanced ? 'stack' : 'pill'}
    >
      <div className="concierge-option-row">
        {options.map((opt) => {
          const isChosen = chosen === opt.label
          return (
            <button
              key={opt.label}
              type="button"
              className="concierge-option-btn"
              data-chosen={isChosen ? 'true' : 'false'}
              data-default={opt.isDefault ? 'true' : undefined}
              disabled={!interactive}
              onClick={() => fire(opt.label)}
            >
              <span className="concierge-option-label">
                {isChosen && (
                  <Check
                    size={12}
                    strokeWidth={2.5}
                    className="concierge-option-check"
                    aria-hidden
                  />
                )}
                <span>{opt.label}</span>
                {opt.isDefault && (
                  <span className="concierge-option-default-tag">default</span>
                )}
              </span>
              {opt.rationale && (
                <span className="concierge-option-rationale">{opt.rationale}</span>
              )}
            </button>
          )
        })}
      </div>
      {interactive && (
        <input
          type="text"
          className="concierge-option-input"
          placeholder="or type a different answer"
          value={other}
          onChange={(e) => setOther(e.target.value)}
          onKeyDown={onOtherKey}
        />
      )}
    </div>
  )
}
